import { useCallback, useMemo } from 'react';
import { EntityForm } from '../form.types';
import { DeliveryContainerSettingsForm } from './form';
import { DeliveryContainerSettingsFormOutput } from './form-output';
import { DeliveryContainerSettingsFormSchema } from './form-schema';
import { deliveryContainerSettingsFormUISchema } from './ui-schema';
import { useQueryGetLocker, useMutationEditDeliveryContainer } from '../../../lockers/lockers.queries';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';

export function useEditDeliveryContainerForm(
  lockerID?: string,
  title?: string,
  _formData?: DeliveryContainerSettingsForm
): EntityForm | null {
  const lockerResult = useQueryGetLocker(lockerID ?? '', !!lockerID);
  const deliveryContainerMutation = useMutationEditDeliveryContainer();
  const { hasAllFeatures: canEdit } = useHasUIFeatures(['internal-groundfloor']);

  const defaultValues = useMemo(() => {
    if (!lockerResult.data) return {} as DeliveryContainerSettingsForm;
    return DeliveryContainerSettingsFormOutput.FromLockerDto(lockerResult.data);
  }, [lockerResult.data]);

  const initialFormOutput = useMemo(() => {
    return new DeliveryContainerSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  const showSize = !!lockerResult.data?.sizeName;
  const formSchema = useMemo(() => DeliveryContainerSettingsFormSchema.Build(showSize), [showSize]);

  const onSubmit = useCallback(
    async (formOutput: DeliveryContainerSettingsFormOutput) => {
      if (!lockerID) return;
      const result = await deliveryContainerMutation.mutateAsync({
        deliveryContainerID: lockerID,
        dto: DeliveryContainerSettingsFormOutput.ToEditLockerDto(formOutput)
      });
      await lockerResult.refetch();
      return { entityId: result.id };
    },
    [lockerID, deliveryContainerMutation, lockerResult]
  );

  if (!lockerID) return null;

  return {
    isLoading: lockerResult.isLoading,
    title: title ?? 'Delivery Container Settings',
    subtitle: lockerID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: deliveryContainerSettingsFormUISchema,
    getOutput(data) {
      return new DeliveryContainerSettingsFormOutput(data as DeliveryContainerSettingsForm);
    },
    onSubmit,
    isLocked: !canEdit,
    lockedMessage: canEdit ? null : 'Editing delivery container settings requires internal Groundfloor access'
  };
}
